import type { Medication, MedRequest, ParsedPatientScrData, PatientType } from '@/types/data'
import type { MedRequestMutationFnProps } from '@/lib/api/med-requests/action'
import { differenceInYears, format, isAfter, parseISO, startOfDay, isValid } from 'date-fns'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { type ExtraTreatment, useMedicationRequestStore } from './stores/medication-request'
import { approvedStatuses, MPS_CATEGORY_HASH, MPS_MAX_TREATMENTS, onHoldStatuses } from './constants/med-requests'
import { toast } from '@/hooks/use-toast'
import { useFlag } from '@unleash/proxy-client-react'
import { parseQuestionnaire } from '@/hooks/useQuestionnaire'
import { useMemo } from 'react'
import { jsonSafeParse } from '@/utils/jsonSafeParse'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getOrderStatusBadgeClasses(status: string) {
  const greenStatuses = [...approvedStatuses, 'Results Shared']

  const amberStatuses = onHoldStatuses

  const neutralStatuses = ['Created', 'Clinical Review']

  if (greenStatuses.includes(status)) {
    return 'border-[#388E10] bg-[rgba(225,241,218,0.50)] text-[#388E10]'
  }

  if (amberStatuses.includes(status)) {
    return 'border-[#CC8800] bg-[#FFF1D6] text-[#CC8800]'
  }
  if (neutralStatuses.includes(status)) {
    return 'border-[#4E545E] bg-[rgba(255,255,255,0.50)] text-[#24272B]'
  }

  return 'border-[#FF4D1A] bg-[rgba(255,224,214,0.50)] text-[#FF4D1A]'
}

export function getInvestigationStatusBadgeClasses(status: string) {
  const greenStatuses = ['Results ready']
  const amberStatuses = ['Started', 'Pending']

  // 'Closed', 'Suspended' and any unrecognized stage fall through to a visible
  // neutral badge rather than an invisible (background-less) one.
  const background = greenStatuses.includes(status)
    ? 'bg-[#388E10]'
    : amberStatuses.includes(status)
      ? 'bg-amber-500'
      : 'bg-[#4E545E]'

  return cn('text-[#FAFAFB]', background)
}

export function getPatientAgeText(dateOfBirth?: string) {
  if (!dateOfBirth) return '-'
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  const age = differenceInYears(today, birthDate)
  return `${age} (${dateOfBirth.split('-').reverse().join('/')})`
}

export function isMedRequestInEditableStage(stage: string) {
  const editableStages = [
    'Clinical Review',
    'Approved for Future',
    'Approved',
    'Clinical Further Answers Needed',
    'Ready For Fulfillment'
  ].map(entry => entry.toLowerCase())

  return editableStages.includes(stage.toLowerCase())
}

export function getAiRespTextClassName({ allText, index }: { allText: string[]; index: number }) {
  const origin = allText[index].trim()
  if (origin[0] === '-' || /^([a-z]\))/.test(origin)) {
    for (let j = index - 1; j > 0; j--) {
      if (allText[j][0] === '\n' || allText[j] === '') {
        break
      }

      const target = allText[j].trim()
      if (/^(\d+\.|[a-z]\)|-)/.test(target) && allText[index][0] !== target[0]) {
        return 'pl-6'
      }
    }
    return 'pl-4'
  }

  if (/^(\d+\.|\*)/.test(origin)) {
    return 'pl-4 '
  }

  return 'my-1'
}

export function calculateTotalHoldReasonLength(
  currentMessage: string,
  existingReasonForHold: string | null,
  userFullName: string
): number {
  const timestamp = 'dd/mm/yyyy hh:mm'

  const userInfo = `[${userFullName}] [${timestamp}] `

  const separator = existingReasonForHold ? '\n\n' : ''

  const totalLength = (existingReasonForHold?.length || 0) + separator.length + userInfo.length + currentMessage.length

  return totalLength
}

export function validateHoldReasonLength(
  contactMessage: string,
  existingReasonForHold: string | null,
  userFullName: string
): { isValid: boolean; errorMessage?: string } {
  const totalLength = calculateTotalHoldReasonLength(contactMessage, existingReasonForHold, userFullName)

  if (totalLength > 1900) {
    return {
      isValid: false,
      errorMessage: 'Hold reason must be less than 1900 characters in total (including existing history)'
    }
  }

  return { isValid: true }
}

// The API's own limits on medication directions and on an approval justification.
const MAX_MEDICATION_DIRECTIONS = 2000
const MAX_APPROVAL_JUSTIFICATION = 200

/** Whether medication directions fit the API's limit; toasts when they don't. */
export function checkMedicationDirectionsLength(direction: string): boolean {
  if (direction.length <= MAX_MEDICATION_DIRECTIONS) return true

  toast({
    title: 'Medication directions too long',
    description: `Medication directions must be less than ${MAX_MEDICATION_DIRECTIONS} characters in total`
  })
  return false
}

/** Whether an approval justification fits the API's limit; toasts when it doesn't. */
export function checkApprovalJustificationLength(justification: string): boolean {
  if (justification.length <= MAX_APPROVAL_JUSTIFICATION) return true

  toast({
    title: 'Approval justification too long',
    description: `Justification must be less than ${MAX_APPROVAL_JUSTIFICATION} characters in total`
  })
  return false
}

/** Whether an action's directions and reason fit the API's limits; toasts the first that doesn't. */
export function checkMedRequestActionLengths({
  action,
  reason = '',
  extraDirectionsForMedication = ''
}: Pick<MedRequestMutationFnProps, 'action' | 'reason' | 'extraDirectionsForMedication'>): boolean {
  if (!checkMedicationDirectionsLength(extraDirectionsForMedication)) return false

  if (action === 'approve' && !checkApprovalJustificationLength(reason)) return false

  if (action === 'reject' && reason.length > 1900) {
    toast({
      title: 'Rejection reason too long',
      description: 'Rejection reason must be less than 1900 characters in total'
    })
    return false
  }

  if (action === 'hold' && reason.length > 1900) {
    toast({
      title: 'Contact message too long',
      description: 'Contact message must be less than 1900 characters in total'
    })
    return false
  }

  return true
}

/** Checks the store's message for `action`, with the opened request's directions unless given another treatment's. */
export function medReqFieldValidationNotifications(
  action: 'approve' | 'reject' | 'hold',
  medicationDirection = useMedicationRequestStore.getState().medicationDirection
): boolean {
  const { justification, rejectionReason, contactMessage } = useMedicationRequestStore.getState()
  const reason = { approve: justification, reject: rejectionReason, hold: contactMessage }[action]

  return checkMedRequestActionLengths({ action, reason, extraDirectionsForMedication: medicationDirection })
}

export function getPreviousOrderDateToUse(order: MedRequest) {
  return new Date(order?.lastActionTakenAt || order?.creationDate)
}

/** parseISO, but null for a missing or unparseable value instead of an Invalid Date. */
export function parseValidDate(value?: string | null): Date | null {
  if (!value) return null
  const date = parseISO(value)
  return isValid(date) ? date : null
}

export function formatStringISOToFullDate(timestamp: string) {
  const date = parseISO(timestamp)
  return format(date, 'd MMMM yyyy hh:mm aa')
}

// It doesnt adjust the tz as its already being done in the BE
export function getCurrentUKDate() {
  const dateObject = new Date()
  const ukTime = dateObject.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })

  // Convert to ISO format (YYYY-MM-DDTHH:MM:SS+00:00)
  const [date] = ukTime.split(', ')
  const [day, month, year] = date.split('/')

  dateObject.setFullYear(Number(year))
  dateObject.setMonth(Number(month) - 1) // months are 0-indexed
  dateObject.setDate(Number(day))

  return dateObject
}

/** Whether an issue date is approved "for future": later than now, as the footer label and batch approval judge it. */
export function isFutureIssueDate(date: Date) {
  return isAfter(date, new Date())
}

const FUTURE_DATE_NOTE = /\nMedication future date:.*\n/g

/** The pharmacist note with its "Medication future date" line matching `issueDate`; added from tomorrow on. */
export function withFutureDateNote(pharmacist: string | null | undefined, issueDate?: Date) {
  const note = pharmacist?.replace(FUTURE_DATE_NOTE, '')
  if (!issueDate || !isAfter(startOfDay(issueDate), startOfDay(new Date()))) return note

  return `${note ?? ''}\nMedication future date: ${issueDate.toLocaleString('en-GB', {
    dateStyle: 'full',
    timeStyle: 'long'
  })}\n`
}

export function isTodayInUK(date: Date) {
  const ukDate = getCurrentUKDate()
  return (
    date.getDate() === ukDate.getDate() &&
    date.getMonth() === ukDate.getMonth() &&
    date.getFullYear() === ukDate.getFullYear()
  )
}

export function convertDateToISOWithoutConverting(date: Date) {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX")
}

export function isMedRequestSubscription(subFrequency: string | null) {
  return !(!subFrequency || subFrequency === 'One Time Purchase')
}

// MPS = MultiProduct Subscription. `evaroType` mirrors `wp_wc_orders.evaro_type`: only
// 'mps_med_order' (a subscription medication's own order) surfaces as a med request — the
// subscription order itself ('mps') and the order it was bought as ('mps_order') never do. The
// API instead attaches each medication's mirror item on the subscription order as `mpsHash`
// (the swap/remove target); med-req /update uses the rows' own ids. Matches the API's
// MPS_EVARO_TYPES (NodeAPI med-reqs-v2).
const MPS_EVARO_TYPES = ['mps_med_order']

export function isMpsMedRequest(medRequest?: MedRequest | null) {
  return !!medRequest?.evaroType && MPS_EVARO_TYPES.includes(medRequest.evaroType.toLowerCase())
}

// A multi product order's request comes with its order's other items (`orderMedRequests`). MPS
// requests never are: they're decided through the subscription sync.
export function isMultiProductOrderMedRequest(medRequest?: MedRequest | null) {
  return !!medRequest?.orderMedRequests?.length && !isMpsMedRequest(medRequest) && !isLinkMedRequest(medRequest)
}

// LINK orders carry the `#LINK#` marker in their order number — the same marker PHP
// `order\is::from_subscription_by_order_number()` matches on.
export function isLinkMedRequest(medRequest?: MedRequest | null) {
  return !!medRequest?.orderNumber?.includes('#LINK#')
}

// Treatment 1 never sits in `extraTreatments` (it lives on the flat store fields), so the cap
// counts it as +1. The single place the limit formula lives: the store's addExtraTreatment guard
// and the "Add another treatment" gating both call this.
export function isMpsTreatmentLimitReached(extraTreatments: readonly ExtraTreatment[]) {
  return extraTreatments.length + 1 >= MPS_MAX_TREATMENTS
}

// A freshly added card starts empty (no medication, blank direction) and the approve sync would
// have nothing to add for it — so approval waits until every extra card carries both a product
// and directions, mirroring the footer's own Treatment 1 checks (`isMedicationMissing`,
// `missingDirections`).
export function hasIncompleteMpsTreatment(extraTreatments: readonly ExtraTreatment[]) {
  return extraTreatments.some(treatment => !treatment.medication || !treatment.direction.trim())
}

// The placeholder product an MPS subscription is bought as (e.g. "Menopause Optimization"). It never
// surfaces as a med request (PHP keeps its order item at 'wc-item-created', which the API's base
// WHERE excludes) — but the medication catalogue still lists it, so it has to be hidden from the
// treatment picker options. Display-form labels; matching is lowercased at runtime. Both
// spellings covered.
const MPS_PLACEHOLDER_PRODUCT_NAMES = ['Menopause Optimization', 'Menopause Optimisation']

export function isMpsPlaceholderProduct(productName?: string | null) {
  if (!productName) return false

  const name = productName.toLowerCase()
  return MPS_PLACEHOLDER_PRODUCT_NAMES.map(label => label.toLowerCase()).some(label => name.includes(label))
}

// On an MPS order the treatment picker is scoped to one product category: only catalogue rows
// carrying MPS_CATEGORY_HASH are offered. Rows with no category data at all are excluded too —
// the absence of the category is what disqualifies them, not a missing column.
export function isMpsCategoryMedication(medication?: Pick<Medication, 'categoryHashes'> | null) {
  return Boolean(medication?.categoryHashes?.includes(MPS_CATEGORY_HASH))
}

export function getMedRequestSubscriptionText(subFrequency: string | null) {
  if (!subFrequency) return ''

  return subFrequency.replace(/_/g, ' ')
}

export function isMedRequestDental(medRequest?: MedRequest): boolean {
  if (!medRequest?.questionaire || medRequest.questionaire === 'null') return false

  const parsedQuestionaire = (parseQuestionnaire(medRequest.questionaire, {}) ?? []).map(entry => ({
    ...entry,
    question: entry.title,
    answer: entry.answer
  }))

  const questions = parsedQuestionaire.map(q => q.question.toLowerCase())
  for (const q of [
    'are you currently suffering from a dental infection affecting your teeth or gums?',
    'which of the following symptoms are you currently experiencing with your tooth?',
    'are you experiencing any of the following additional symptoms?',
    'are you allergic to any medication?',
    'are you currently taking any medication?',
    'have you been diagnosed with any medical or mental health conditions?',
    'are you pregnant or breastfeeding, think you may be pregnant or planning to have a baby?',
    'have you booked an appointment with a dentist to examine the affected teeth?',
    'is there any other information you would like to share with our clinical team?'
  ]) {
    if (!questions.includes(q)) {
      return false
    }
  }

  return true
}

export function useConsultationSummaryFlag(medRequest?: MedRequest): {
  enabled: boolean
  isDental: boolean
} {
  const isDentalFlagEnabled = useFlag('ai_dental')
  const isGeneralFlagEnabled = useFlag('consultation-ai-general-prompt')
  const isDental = useMemo(() => isMedRequestDental(medRequest), [medRequest])

  return {
    enabled:
      !isDental && isGeneralFlagEnabled
        ? true
        : medRequest?.orderedItem?.category === 'Dental Infection' &&
          medRequest?.isPriority === false &&
          isDentalFlagEnabled,
    isDental
  }
}

/**
 * Unescapes HTML entities in a text string.
 *
 * Converts HTML entities like &#039; (apostrophe), &quot; (quote), &lt; (less than),
 * &gt; (greater than), and &amp; (ampersand) back to their original characters.
 *
 * @param text - The text string containing HTML entities to unescape
 * @returns The unescaped text with HTML entities converted to their original characters
 *
 * @example
 * ```typescript
 * htmlUnescape("It&#039;s working!") // Returns: "It's working!"
 * htmlUnescape("This &quot;quote&quot; works") // Returns: "This "quote" works"
 * ```
 */
export function htmlUnescape(text: string) {
  const doc = new DOMParser().parseFromString(text, 'text/html')
  return doc.documentElement.textContent
}

/**
 * Extracts the SCR data from the patient object.
 *
 */
export function extractPatientScrData(patient?: PatientType): ParsedPatientScrData | null {
  if (!patient?.scrData || patient.scrData === 'Summary Care Record not found') {
    return null
  }

  const parsedScrData = jsonSafeParse(patient.scrData) || { INVALID_SCR: patient.scrData }
  const lastUpdated = (parsedScrData as { summaryCareRecord?: { lastUpdated?: string | null } })?.summaryCareRecord
    ?.lastUpdated

  return {
    ...parsedScrData,
    lastUpdated: lastUpdated ? new Date(lastUpdated) : null
  }
}
